import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { AppConfig } from '®shared/config';
import {
  ContainerModes,
  PublishListAction,
  PublishListAndRequestReviewAction,
} from '®shared/container-structure';
import { UserPermissions } from '®shared/permissions';
import {
  EditableList,
  ListPublishingState,
  ReadingListDetails,
} from '®shared/reading-list';
import { RequestReviewShowModalAction } from '®shared/reviews/request-review';
import { WindowShimService } from '®shared/window-shim';

import {
  showPublishButton,
  showRequestReviewButton,
  showReviewAndPublishButton,
} from '../action-buttons';

@Component({
  selector: 'rl-sticky-footer-bar',
  styleUrls: ['sticky-footer-bar.component.scss'],
  templateUrl: './sticky-footer-bar.component.html',
})
export class StickyFooterBarComponent implements OnInit, OnDestroy {
  constructor(
    private appConfig: AppConfig,
    private cd: ChangeDetectorRef,
    private window: WindowShimService,
  ) {}

  @Input() containerMode: ContainerModes;
  @Input() editableList: EditableList;
  @Input() list: ReadingListDetails;
  @Input() listPublishingStatus: ListPublishingState;
  @Input() permissions: UserPermissions;
  @Input() queueHasPublishAction?: boolean;
  @Input() queueSize: number;
  @Output() handlePublish: EventEmitter<PublishListAction> = new EventEmitter();
  @Output()
  showRequestReviewModalAndPublishList: EventEmitter<PublishListAndRequestReviewAction> =
    new EventEmitter();
  @Output()
  showRequestReviewModal = new EventEmitter<RequestReviewShowModalAction>();
  @Output() noChangesToPublish = new EventEmitter();

  private eventSub: Subscription;

  showStickyFooterBar = false;
  reviewWorkflow = this.appConfig.tenant.featureFlags.reviewWorkflow;

  get isEditMode() {
    return this.containerMode === ContainerModes.Edit;
  }

  ngOnInit() {
    this.eventSub = fromEvent(window, 'scroll')
      .pipe(
        debounceTime(300),
        tap(() => this.viewportChanged()),
      )
      .subscribe();

    this.eventSub.add(
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(300),
          tap(() => this.viewportChanged()),
        )
        .subscribe(),
    );

    this.eventSub.add(
      fromEvent(window, 'keydown')
        .pipe(
          debounceTime(300),
          tap(() => this.viewportChanged()),
        )
        .subscribe(),
    );
  }

  viewportChanged() {
    const scrollOffset = this.window.pageYOffset;
    const viewportHeight = this.window.innerHeight;
    const showStickyFooterBar = scrollOffset > viewportHeight / 4;

    if (showStickyFooterBar !== this.showStickyFooterBar) {
      this.showStickyFooterBar = showStickyFooterBar;
      this.cd.detectChanges();
    }
  }

  scrollToTop() {
    const h1 = this.window.document.getElementById('c-view-title__title');
    if (h1) {
      h1.scrollIntoView();
      h1.focus();
    }
  }

  requestReview() {
    if (!this.editableList.hasUnpublishedChanges) {
      return;
    }
    this.showRequestReviewModal.emit(
      new RequestReviewShowModalAction({ listId: this.editableList.id }),
    );
  }

  reviewAndPublish() {
    if (!this.editableList.hasUnpublishedChanges) {
      return;
    }
    this.showRequestReviewModalAndPublishList.emit(
      new PublishListAndRequestReviewAction({ listId: this.editableList.id }),
    );
  }

  publish(): void {
    if (!this.editableList.hasUnpublishedChanges) {
      this.noChangesToPublish.emit();
      return;
    }
    this.handlePublish.emit(
      new PublishListAction({ listId: this.editableList.id }),
    );
  }

  ngOnDestroy() {
    this.eventSub.unsubscribe();
  }

  get showPublishButton(): boolean {
    return showPublishButton(
      this.containerMode,
      this.list,
      this.editableList,
      this.reviewWorkflow,
      this.queueHasPublishAction,
      this.permissions,
    );
  }

  get showRequestReviewButton(): boolean {
    return showRequestReviewButton(
      this.containerMode,
      this.editableList,
      this.permissions,
      this.reviewWorkflow,
    );
  }

  get showReviewAndPublishButton(): boolean {
    return showReviewAndPublishButton(
      this.containerMode,
      this.editableList,
      this.permissions,
      this.reviewWorkflow,
    );
  }
}
